import { CertificateDesignerStore, CertificateTemplate } from "./certificateTypes";
import {
  cloneCertificateTemplate,
  DEFAULT_CERTIFICATE_TEMPLATES,
  DEFAULT_TEMPLATE_ID,
} from "./certificateTemplates";

const STORAGE_KEY = "our_dental.certificatePrint.templates.v3";

const REQUIRED_FOOTER_IDS = [
  "doctorName",
  "qualifications",
  "designation",
  "slmcNumber",
] as const;

function builtinStore(): CertificateDesignerStore {
  return {
    templates: DEFAULT_CERTIFICATE_TEMPLATES.map((template) =>
      cloneCertificateTemplate(template, { id: template.id, name: template.name })
    ),
    activeId: DEFAULT_TEMPLATE_ID,
    defaultId: DEFAULT_TEMPLATE_ID,
  };
}

function ensureFooterDoctorFields(
  elements: CertificateTemplate["elements"],
  builtin: CertificateTemplate
): CertificateTemplate["elements"] {
  const have = new Set(elements.map((el) => el.id));
  const missing = REQUIRED_FOOTER_IDS.filter((id) => !have.has(id));
  if (!missing.length) return elements;
  const extras = builtin.elements
    .filter((el) => missing.includes(el.id as (typeof REQUIRED_FOOTER_IDS)[number]))
    .map((el) => ({ ...el, visible: true }));
  return [...elements, ...extras];
}

function hydrateTemplate(raw: CertificateTemplate): CertificateTemplate {
  const builtin =
    DEFAULT_CERTIFICATE_TEMPLATES.find((item) => item.id === raw.id) ||
    DEFAULT_CERTIFICATE_TEMPLATES[0];
  const elements =
    Array.isArray(raw.elements) && raw.elements.length > 0
      ? ensureFooterDoctorFields(
          raw.elements.map((el) => ({ ...el })),
          builtin
        )
      : builtin.elements.map((el) => ({ ...el }));
  return {
    id: raw.id || `tpl-${Date.now()}`,
    name: raw.name || builtin.name,
    page: { ...builtin.page, ...raw.page },
    elements,
  };
}

export function isCertificateDesignerStore(raw: unknown): raw is CertificateDesignerStore {
  return Boolean(
    raw &&
      typeof raw === "object" &&
      Array.isArray((raw as CertificateDesignerStore).templates)
  );
}

export function hydrateCertificateStore(raw: unknown): CertificateDesignerStore {
  if (isCertificateDesignerStore(raw) && raw.templates.length) {
    const templates = raw.templates.map(hydrateTemplate);
    const activeId = templates.some((item) => item.id === raw.activeId)
      ? raw.activeId
      : templates[0].id;
    const defaultId = templates.some((item) => item.id === raw.defaultId)
      ? raw.defaultId
      : templates[0].id;
    return { templates, activeId, defaultId };
  }
  return builtinStore();
}

export function loadCertificateStore(): CertificateDesignerStore {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return builtinStore();
    return hydrateCertificateStore(JSON.parse(raw));
  } catch {
    return builtinStore();
  }
}

export function saveCertificateStore(store: CertificateDesignerStore) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    /* ignore quota / private mode */
  }
}

export function createTemplateId() {
  return `tpl-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

export function findTemplate(
  templates: CertificateTemplate[],
  id: string
): CertificateTemplate {
  return templates.find((item) => item.id === id) ?? templates[0];
}

export function getPrintableCertificateTemplate(templateId?: string): CertificateTemplate {
  const store = loadCertificateStore();
  return findTemplate(store.templates, templateId || store.defaultId || store.activeId);
}

function waitForPaint() {
  return new Promise<void>((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
}

export async function printCertificateSheet(paper: HTMLElement | null) {
  if (!paper) return;

  const host = document.createElement("div");
  host.id = "certificate-print-host";
  const clone = paper.cloneNode(true) as HTMLElement;
  clone.removeAttribute("id");
  clone.classList.add("certificate-print-sheet");
  host.appendChild(clone);
  document.body.appendChild(host);
  document.documentElement.classList.add("is-certificate-printing");
  await waitForPaint();

  try {
    window.print();
  } finally {
    document.documentElement.classList.remove("is-certificate-printing");
    host.remove();
  }
}
